import React, { useMemo, useCallback } from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import media from "styled-media-query";
import styled from "styled-components";

import { State } from "src/state/ducks";
import { NotificationEntity } from "src/firebase/db/Notifications";

import StyleConfig from "src/constants/StyleConfig";
import { formatDate } from "src/helpers/UtilityHelper";

import Button from "src/components/atoms/button/Button";

type Props = {
  notification: NotificationEntity;
  onBack?: (payload: NotificationEntity) => void;
  onSubmit?: (payload: NotificationEntity) => void;
  isFetching: boolean;
  hideActions?: boolean;
  forceShowTemplateTitle?: boolean;
};

const NotificationConfirmForm = (props: Props) => {
  const {
    notification,
    onSubmit,
    onBack,
    isFetching,
    hideActions,
    forceShowTemplateTitle,
  } = props;

  const {
    needs_template,
    template_title,
    pickup_supplement,
    scheduled_time,
    can_order_time,
    last_pickup_time,
    order_breads,
  } = notification;

  const selectedOrderBreads = useMemo(
    () => order_breads.filter((v) => v.selected),
    [order_breads]
  );

  const wrapOnSubmit = useCallback(() => {
    if (onSubmit) {
      onSubmit(notification);
    }
  }, [onSubmit, notification]);

  const wrapOnBack = useCallback(() => {
    if (onBack) {
      onBack(notification);
    }
  }, [onBack, notification]);

  return (
    <Styled>
      {(needs_template || forceShowTemplateTitle) && (
        <Item>
          <Label>テンプレート名</Label>
          <Value>{template_title}</Value>
        </Item>
      )}
      <Item>
        <Label>焼き上がりのお知らせ時刻</Label>
        <Value>{formatDate(scheduled_time, "M月D日 HH:mm")}</Value>
      </Item>
      <Item>
        <Label>通知するパン</Label>
        {selectedOrderBreads.map((orderBread) => (
          <OrderBread key={orderBread.bread_id}>
            <div className="orderBread-image">
              <img src={orderBread.image} alt={orderBread.name} />
            </div>
            <div className="orderBread-name">
              <span>{orderBread.name}</span>
            </div>
            <div className="orderBread-quantity">
              <p>
                <span>販売個数</span>
                <span>{orderBread.stock}個</span>
              </p>
              <p>
                <span>購入上限数</span>
                <span>{orderBread.maximum_order}個</span>
              </p>
            </div>
          </OrderBread>
        ))}
      </Item>
      <Item>
        <Label>ラストオーダー時刻</Label>
        <Value>{formatDate(can_order_time, "M月D日 HH:mm")}</Value>
      </Item>
      <Item>
        <Label>最終受取時刻</Label>
        <Value>{formatDate(last_pickup_time, "M月D日 HH:mm")}</Value>
      </Item>
      <Item>
        <Label>パンの受け渡しに関する注意事項</Label>
        <Value>
          <b>{pickup_supplement}</b>
        </Value>
      </Item>
      {!hideActions && (
        <Controller>
          <PrevButtonWrapper>
            <Button
              text="修正する"
              buttonColor="white"
              isDisabled={isFetching}
              clickEvent={wrapOnBack}
            />
          </PrevButtonWrapper>
          <NextButtonWrapper>
            <Button
              isDisabled={isFetching}
              clickEvent={wrapOnSubmit}
              text="お知らせする"
            />
          </NextButtonWrapper>
        </Controller>
      )}
    </Styled>
  );
};

const Styled = styled.div`
  box-sizing: border-box;
  padding: ${StyleConfig.HELPER.M16} 0;
`;
const Item = styled.div`
  &:not(:first-child) {
    margin-top: 32px;
  }
`;
const Label = styled.p`
  font-size: 14px;
  color: ${StyleConfig.COLOR.TEXT_COLOR};
  background: ${StyleConfig.COLOR.INPUT_BORDER};
  padding: 9px ${StyleConfig.HELPER.M16};
`;
const Value = styled.p`
  font-size: 16px;
  line-height: ${24 / 16};
  font-weight: bold;
  color: ${StyleConfig.COLOR.TEXT_COLOR};
  padding: 16px ${StyleConfig.HELPER.M16} 0;
`;
const OrderBread = styled.div`
  line-height: ${24 / 16};
  font-weight: bold;
  color: ${StyleConfig.COLOR.TEXT_COLOR};
  padding: 0 ${StyleConfig.HELPER.M16};
  ${media.greaterThan("medium")`
    padding: 20px ${StyleConfig.HELPER.M16};
  `};
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  &:not(:last-child) {
    border-bottom: 1px solid ${StyleConfig.COLORS.GRAY6};
  }

  .orderBread-image {
    width: 84px;
    height: 84px;
    overflow: hidden;
    position: relative;
    img {
      position: absolute;
      top: 50%;
      left: 50%;
      -webkit-transform: translate(-50%, -50%);
      -ms-transform: translate(-50%, -50%);
      transform: translate(-50%, -50%);
      width: 100%;
      height: auto;
    }
    flex: 20%;
  }
  .orderBread-name {
    margin-right: auto;
    padding-left: 10px;
    flex: 40%;
  }
  .orderBread-quantity {
    font-weight: normal;
    margin-left: auto;
    flex: 40%;
    text-align: right;
    p {
      display: inline-flex;
      width: 100%;
      span:first-child {
        flex-grow: 2;
      }
      span:last-child {
        width: 40px;
      }
    }
  }
`;
const Controller = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${StyleConfig.HELPER.M16};
  margin-top: 52px;
`;
const PrevButtonWrapper = styled.div`
  width: ${(131 / 343) * 100}%;
`;
const NextButtonWrapper = styled.div`
  width: ${(200 / 343) * 100}%;
`;

const mapStateToProps = (state: State) => ({});

const mapDispatchToProps = (dispatch: Dispatch) => ({});
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(NotificationConfirmForm);
